import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Box, TextField,
  Chip, Tooltip, CircularProgress
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import PeopleIcon from "@material-ui/icons/People";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import LinkIcon from "@material-ui/icons/Link";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import usePlans from "../../hooks/usePlans";
import { toast } from "react-toastify";
import moment from "moment";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
  },
  header: {
    marginBottom: theme.spacing(3),
    fontWeight: 700,
  },
  statCard: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "12px",
    minHeight: "130px",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    color: "#fff",
    "& .icon": {
      position: "absolute",
      right: 16,
      bottom: 16,
      opacity: 0.3,
      fontSize: "4rem",
    }
  },
  cardBalance: {
    background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
  },
  cardDiscount: {
    background: "linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)",
  },
  cardClients: {
    background: "linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)",
  },
  cardLabel: {
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: 1,
    opacity: 0.85,
    marginBottom: theme.spacing(1),
  },
  cardValue: {
    fontSize: "2.5rem",
    fontWeight: 700,
    lineHeight: 1,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  linkBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#f0f4ff",
    border: `1px solid ${theme.palette.primary.light}`,
    padding: theme.spacing(1.5, 2),
    borderRadius: "10px",
    marginTop: theme.spacing(1),
    wordBreak: "break-all",
    gap: theme.spacing(1),
  },
  tableContainer: {
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderRadius: "10px",
    "& th": {
      fontWeight: 700,
      backgroundColor: theme.palette.type === "dark" ? "#333" : "#f5f5f5",
    }
  },
  chip: {
    fontWeight: 600,
  },
  amountPositive: {
    color: "#4caf50",
    fontWeight: 700,
  },
  amountNegative: {
    color: "#f44336",
    fontWeight: 700,
  },
  renewBtn: {
    fontWeight: 700,
    fontSize: "0.7rem",
    background: "linear-gradient(45deg, #FF512F 0%, #F09819 100%)",
    color: "#fff",
    border: "0",
    boxShadow: "0 4px 15px rgba(240, 152, 25, 0.4)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(45deg, #F09819 0%, #FF512F 100%)",
      boxShadow: "0 6px 20px rgba(240, 152, 25, 0.6)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      background: "#e0e0e0",
      color: "#9e9e9e",
      boxShadow: "none",
    }
  },
  payLinkSection: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "12px",
    padding: theme.spacing(2.5),
    marginTop: theme.spacing(3),
  }
}));

const ResellerPanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [renewingId, setRenewingId] = useState(null);
  const [data, setData] = useState({
    resellerBalance: 0,
    discountPercentage: 0,
    balanceExpiration: null,
    resellerPaymentLink: "",
    referredCompanies: [],
    transactions: [],
  });
  const [paymentLink, setPaymentLink] = useState("");
  const [plans, setPlans] = useState([]);
  const { list } = usePlans();
  const [selectedPlans, setSelectedPlans] = useState({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);

  const fetchPlans = async () => {
    try {
      const response = await list();
      setPlans(response.plans || response || []);
    } catch (err) {
      toast.error("Error al cargar planes");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/reseller/dashboard");
      setData(response.data);
      setPaymentLink(response.data.resellerPaymentLink || "");
    } catch (err) {
      toast.error(i18n.t("resellerPanel.errors.loadError") || "Error al cargar datos");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user?.companyId) {
      fetchData();
      fetchPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  const allCompanies = React.useMemo(() => {
    if (!user || !user.company) return data.referredCompanies;
    const selfCompany = {
      ...user.company,
      name: `${user.company.name} (Tú)`
    };
    // Ensure we don't duplicate if backend happens to return it
    const filtered = data.referredCompanies.filter(c => c.id !== selfCompany.id);
    return [selfCompany, ...filtered];
  }, [data.referredCompanies, user]);

  const referralLink = `${window.location.origin}/signup?ref=${user?.companyId}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success(i18n.t("resellerPanel.copied") || "¡Enlace copiado!");
  };

  const handleSavePaymentLink = async () => {
    try {
      await api.put(`/companies/${user.companyId}`, { resellerPaymentLink: paymentLink });
      toast.success(i18n.t("resellerPanel.payLinkSaved") || "Enlace de cobro guardado");
    } catch (err) {
      toast.error(i18n.t("resellerPanel.errors.saveLinkError") || "Error al guardar enlace");
    }
  };

  const handleRenew = async (company) => {
    let planId = selectedPlans[company.id] || company.planId;
    if (!planId) {
      toast.error("Seleccione un plan primero");
      return;
    }
    
    planId = Number(planId);
    
    const selectedPlan = plans.find(p => p.id === planId) || (Array.isArray(plans) && plans.length > 0 ? plans[0] : null);
    if (!selectedPlan) {
      toast.error("Plan no encontrado");
      return;
    }

    let cost = 0;
    if (selectedPlan.amount) {
      cost = parseFloat(selectedPlan.amount.toString().replace(",", ".")) || 0;
    }

    setConfirmModalData({
      company,
      planId,
      selectedPlan,
      cost
    });
    setConfirmModalOpen(true);
  };

  const handleConfirmRenew = async () => {
    if (!confirmModalData) return;
    
    const { company, planId } = confirmModalData;
    setRenewingId(company.id);
    try {
      const res = await api.post(`/reseller/assign-plan/${company.id}`, { planId });
      toast.success(`✅ ${company.name} actualizada. Nuevo saldo: $${res.data.newBalance}`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error al renovar");
    }
    setRenewingId(null);
    setConfirmModalOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} variant="outlined">
        {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" className={classes.header}>
          🏪 {i18n.t("resellerPanel.title") || "Panel Reseller"}
        </Typography>
        <Tooltip title="Actualizar datos">
          <Button size="small" onClick={fetchData} startIcon={<RefreshIcon />}>
            {i18n.t("resellerPanel.refresh") || "Actualizar"}
          </Button>
        </Tooltip>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper className={`${classes.statCard} ${classes.cardBalance}`} elevation={3}>
            <Typography className={classes.cardLabel}>
              {i18n.t("resellerPanel.balance") || "Saldo Comercial"}
            </Typography>
            <Typography className={classes.cardValue}>
              ${parseFloat(data.resellerBalance).toFixed(2)}
            </Typography>
            <Typography variant="caption" style={{ opacity: 0.8, marginTop: 6, display: "block", fontWeight: 500 }}>
              (Inversión Real: ${(data.resellerBalance * (1 - data.discountPercentage / 100)).toFixed(2)})
            </Typography>
            {data.balanceExpiration && (
              <Typography variant="caption" style={{ opacity: 0.8, marginTop: 2, display: "block" }}>
                Vence: {moment(data.balanceExpiration).format("DD/MM/YYYY")}
              </Typography>
            )}
            <AccountBalanceWalletIcon className="icon" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={`${classes.statCard} ${classes.cardDiscount}`} elevation={3}>
            <Typography className={classes.cardLabel}>
              {i18n.t("resellerPanel.discount") || "Tu Descuento / Comisión"}
            </Typography>
            <Typography className={classes.cardValue}>
              {data.discountPercentage}%
            </Typography>
            <TrendingDownIcon className="icon" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={`${classes.statCard} ${classes.cardClients}`} elevation={3}>
            <Typography className={classes.cardLabel}>
              {i18n.t("resellerPanel.referredClients") || "Clientes Referidos"}
            </Typography>
            <Typography className={classes.cardValue}>
              {data.referredCompanies.length}
            </Typography>
            <PeopleIcon className="icon" />
          </Paper>
        </Grid>

        {/* Referral Link */}
        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>
            <LinkIcon fontSize="small" />
            {i18n.t("resellerPanel.referralLink") || "Tu Enlace de Referido"}
          </Typography>
          <Box className={classes.linkBox}>
            <Typography variant="body2" style={{ flex: 1 }}>{referralLink}</Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<FileCopyIcon />}
              onClick={handleCopyLink}
            >
              {i18n.t("resellerPanel.copy") || "Copiar"}
            </Button>
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: "block" }}>
            {i18n.t("resellerPanel.referralLinkHint") || "Comparte este enlace con tus clientes. Quienes se registren con él quedarán vinculados a ti automáticamente."}
          </Typography>
        </Grid>

        {/* Payment Link */}
        <Grid item xs={12}>
          <Box className={classes.payLinkSection}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>
              💳 {i18n.t("resellerPanel.paymentLink") || "Tu Enlace de Cobro (Opcional)"}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              {i18n.t("resellerPanel.paymentLinkHint") || "Si configuras un enlace (ej: Mercado Pago, PayPal, WhatsApp), tus clientes serán dirigidos a él cuando necesiten pagar. Si no lo configuras, verán \"Contacte a su proveedor\"."}
            </Typography>
            <Box display="flex" gap={1} style={{ gap: "10px" }}>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="https://..."
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
              />
              <Button variant="contained" color="primary" onClick={handleSavePaymentLink}>
                {i18n.t("resellerPanel.save") || "Guardar"}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Referred Companies Table */}
      <Box mt={3} width="100%">
        <Typography className={classes.sectionTitle}>
            <PeopleIcon fontSize="small" />
            {i18n.t("resellerPanel.registeredCompanies") || "Empresas Registradas"}
          </Typography>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>{i18n.t("resellerPanel.table.name") || "Nombre"}</TableCell>
                  <TableCell>{i18n.t("resellerPanel.table.email") || "Email"}</TableCell>
                  <TableCell>{i18n.t("resellerPanel.table.phone") || "Teléfono"}</TableCell>
                  <TableCell>{i18n.t("resellerPanel.table.dueDate") || "Vencimiento"}</TableCell>
                  <TableCell>{i18n.t("resellerPanel.table.status") || "Estado"}</TableCell>
                  <TableCell align="center">{i18n.t("resellerPanel.table.actions") || "Acciones"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" style={{ padding: "32px 0" }}>
                      <Typography color="textSecondary">
                        {i18n.t("resellerPanel.noClients") || "Aún no tienes clientes. ¡Comparte tu enlace de referido!"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  allCompanies.map((company) => {
                    const isExpired = new Date(company.dueDate) < new Date();
                    return (
                      <TableRow key={company.id} hover>
                        <TableCell>{company.id}</TableCell>
                        <TableCell><strong>{company.name}</strong></TableCell>
                        <TableCell>{company.email}</TableCell>
                        <TableCell>{company.phone}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{ color: isExpired ? "#f44336" : "inherit" }}
                          >
                            {company.dueDate ? moment(company.dueDate).format("DD/MM/YYYY") : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={company.status ? (i18n.t("resellerPanel.active") || "Activo") : (i18n.t("resellerPanel.inactive") || "Inactivo")}
                            size="small"
                            className={classes.chip}
                            style={{
                              backgroundColor: company.status ? "#e8f5e9" : "#fce4ec",
                              color: company.status ? "#2e7d32" : "#c62828"
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1} style={{ gap: "8px" }}>
                            <TextField
                              select
                              size="small"
                              variant="outlined"
                              SelectProps={{ native: true }}
                              value={selectedPlans[company.id] || company.planId || ""}
                              onChange={(e) => setSelectedPlans({...selectedPlans, [company.id]: e.target.value})}
                              style={{ minWidth: "120px" }}
                            >
                              <option value="" disabled>Seleccione Plan</option>
                              {Array.isArray(plans) && plans.map(p => (
                                <option key={p.id} value={p.id}>{p.name} (${p.amount})</option>
                              ))}
                            </TextField>
                            
                            <Button
                              size="small"
                              variant="contained"
                              className={classes.renewBtn}
                              disabled={renewingId === company.id}
                              onClick={() => handleRenew(company)}
                            >
                              {renewingId === company.id
                                ? <CircularProgress size={16} color="inherit" />
                                : `⟳ Activar Plan`
                              }
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Transaction History */}
        <Box mt={3} width="100%" pb={2}>
          <Typography className={classes.sectionTitle}>
            <AccountBalanceWalletIcon fontSize="small" />
            {i18n.t("resellerPanel.transactionHistory") || "Historial de Transacciones"}
          </Typography>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("resellerPanel.table.date") || "Fecha"}</TableCell>
                  <TableCell>{i18n.t("resellerPanel.table.type") || "Tipo"}</TableCell>
                  <TableCell>{i18n.t("resellerPanel.table.description") || "Descripción"}</TableCell>
                  <TableCell align="right">{i18n.t("resellerPanel.table.amount") || "Monto"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" style={{ padding: "32px 0" }}>
                      <Typography color="textSecondary">
                        {i18n.t("resellerPanel.noTransactions") || "No hay transacciones registradas."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.transactions.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>{moment(t.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                      <TableCell>
                        <Chip
                          label={t.type.toUpperCase()}
                          size="small"
                          style={{
                            backgroundColor: t.type === "recharge" ? "#e3f2fd" : "#fce4ec",
                            color: t.type === "recharge" ? "#1565c0" : "#b71c1c",
                            fontWeight: 700,
                            fontSize: "0.65rem"
                          }}
                        />
                      </TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell align="right">
                        <span className={t.type === "recharge" ? classes.amountPositive : classes.amountNegative}>
                          {t.type === "recharge" ? "+" : "-"}${parseFloat(t.amount).toFixed(2)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <ConfirmationModal
        title="Confirmar Asignación de Plan"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmRenew}
      >
        {confirmModalData && (
          <Typography align="center" style={{ textAlign: "justify", textJustify: "inter-word", margin: "0 auto", display: "block" }}>
            ¿Estás seguro de asignar el plan <strong>"{confirmModalData.selectedPlan.name}"</strong> a la empresa <strong>"{confirmModalData.company.name}"</strong>?
            <br /><br />
            <div style={{ textAlign: "center" }}>
              Costo exacto a descontar: <strong>${parseFloat(confirmModalData.cost).toFixed(2)}</strong>
              <br />
              <em>(Se descontará de tu Saldo Comercial)</em>
            </div>
          </Typography>
        )}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default ResellerPanel;
